'use client';

import { useDialogManager } from '@up2promo-setup-frontend/core/hooks/use-dialog-manager';
import { useHashChecker } from '@up2promo-setup-frontend/core/hooks/use-hash-checker';
import { useSetupFlow } from '@up2promo-setup-frontend/core/hooks/use-setup-flow';
import { useKeyboardNavigation } from '@up2promo-setup-frontend/core/hooks/use-keyboard-navigation';
import { useSetupStore } from '@up2promo-setup-frontend/core/store/setup.store';
import { useDictionary } from '@up2promo-setup-frontend/ui/providers/dictionary.provider';
import { Navigate, Outlet, useParams, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useRef } from 'react';

import { RouteErrorBoundary } from '@up2promo-setup-frontend/components/route-error-boundary';
import { SetupContainer } from '../components/setup-container';
import { SetupHeader } from '../components/setup-header';
import { SetupProgress } from '../components/setup-progress';
import { useRouteStepInfo } from './hooks/use-route-step-info';
import { useSetupNavigation } from './hooks/use-setup-navigation';

export default function SetupLayout() {
  const params = useParams<{ hash?: string }>();
  const [searchParams] = useSearchParams();
  const translations = useDictionary();
  const { openDialog } = useDialogManager();

  // Extract hash from route params and store
  const paramsHash = params.hash;
  const storeHash = useSetupStore((state) => state.hash);

  // Setup flow hook
  const { initialize } = useSetupFlow();

  // Calculate step info from current route
  const { currentRoute, currentStepIndex, maxSteps } = useRouteStepInfo();

  // Navigation helper
  const { goBack: navigateBack } = useSetupNavigation();

  // Track previous route to detect navigation away from congrats
  const previousRouteRef = useRef<string>(currentRoute);
  const targetLink = useSetupStore((state) => state.targetLink);

  // Check if navigating away from congrats page
  useEffect(() => {
    // If we were on congrats and now we're on a different route, redirect
    if (
      previousRouteRef.current === 'congrats' &&
      currentRoute !== 'congrats'
    ) {
      window.location.href = targetLink || 'https://www.up2promo.com';
      return;
    }

    // Update previous route for next render
    previousRouteRef.current = currentRoute;
  }, [currentRoute, targetLink]);

  // Initialize after hash is checked
  const initializationCompleteRef = useRef(false);

  // Memoize onHashSet to prevent infinite re-renders in useHashChecker
  const onHashSet = useCallback(() => {
    if (!initializationCompleteRef.current) {
      initializationCompleteRef.current = true;
      initialize();
    }
  }, [initialize]);

  // Hash checking
  useHashChecker({
    hash: paramsHash,
    openDialog,
    translations,
    onHashSet,
  });

  // Handle back navigation
  const handleBack = () => {
    navigateBack(currentRoute);
  };

  // Keyboard navigation (must be called before any conditional returns)
  useKeyboardNavigation({
    onEscape: handleBack,
    enabled: currentStepIndex >= 0,
  });

  // Guard: If user refreshes on a route past welcome and hash isn't set yet,
  // redirect to welcome (preserving query params like ?t=GO)
  // This prevents showing pages with incomplete state after refresh
  // MUST be after all hooks to maintain hook order
  const isHashNotSet = paramsHash && !storeHash;
  const isNotOnWelcome = currentRoute !== 'welcome';
  if (isHashNotSet && isNotOnWelcome) {
    const basePath = paramsHash ? `/${paramsHash}` : '';
    const search = searchParams.toString();
    const searchString = search ? `?${search}` : '';
    return <Navigate to={`${basePath}/welcome${searchString}`} replace />;
  }

  return (
    <SetupContainer>
      <SetupProgress currentStep={currentStepIndex} maxSteps={maxSteps} />
      <SetupHeader
        currentStep={currentStepIndex}
        maxSteps={maxSteps}
        onBack={
          currentStepIndex > 0 && currentStepIndex < maxSteps - 1
            ? handleBack
            : undefined
        }
      />
      <RouteErrorBoundary>
        <Outlet />
      </RouteErrorBoundary>
    </SetupContainer>
  );
}
